import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, subMonths, startOfMonth, endOfMonth, addMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Building2,
  MoreVertical,
  FileUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useFinancialAccounts } from '@/hooks/useFinancialAccounts';
import { useBankTransactions } from '@/hooks/useBankTransactions';
import { useBankIntegrations } from '@/hooks/useBankIntegrations';
import { useSyncBankTransactions } from '@/hooks/useSyncBankTransactions';
import { useAccountBalanceCheck } from '@/hooks/useAccountBalanceCheck';
import { getAccountTypeIcon, getAccountTypeLabel } from '@/utils/accountTypeLabels';
import { formatCurrency } from '@/utils/formatters';
import { AccountReconciliationPanel } from '@/components/Financial/Accounts/AccountReconciliationPanel';
import { AccountMovementsPanel } from '@/components/Financial/Accounts/AccountMovementsPanel';
import { OFXImportDialog } from '@/components/Financial/Reconciliation/OFXImportDialog';


export default function FinancialAccountDetails() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [ofxDialogOpen, setOfxDialogOpen] = useState(false);

  const { accounts, isLoading: isLoadingAccounts } = useFinancialAccounts();
  const account = accounts.find(a => a.id === accountId);

  const requiresReconciliation = account?.requires_reconciliation ?? false;

  const [activeTab, setActiveTab] = useState('movements');

  const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
  const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

  const { integrations } = useBankIntegrations();
  const { syncTransactions, isSyncing } = useSyncBankTransactions();

  const {
    transactions: bankTransactions,
    pendingCount,
    reconciledCount,
    ignoredCount,
    totalCredits,
    totalDebits,
    refetch: refetchBank,
    isLoading: isLoadingTransactions,
  } = useBankTransactions({
    financialAccountId: accountId,
    startDate,
    endDate,
  });

  // Hook antes dos early returns (rules-of-hooks; réu registrado em §I.138)
  const { isDivergent, difference, calculatedBalance } = useAccountBalanceCheck(accountId!);

  const accountIntegration = integrations?.find(i =>
    account?.bank_integration_id === i.id ||
    (account?.bank_name?.toLowerCase().includes('inter') && i.provider === 'inter') ||
    (account?.bank_name?.toLowerCase().includes('cora') && i.provider === 'cora')
  );

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1));
  };

  const handleSync = async () => {
    if (!accountId || !accountIntegration) return;
    try {
      await syncTransactions({
        financial_account_id: accountId,
        provider: accountIntegration.provider,
        start_date: startDate,
        end_date: endDate,
      });
      refetchBank();
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
    }
  };

  if (isLoadingAccounts) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">Conta não encontrada</p>
        <Button variant="outline" onClick={() => navigate('/dashboard/settings?tab=financeiro&subtab=config-financeiro&financial_tab=accounts')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
      </div>
    );
  }

  const pendingValue = bankTransactions
    .filter(t => t.reconciliation_status === 'pending')
    .reduce((sum, t) => sum + Math.abs(t.value), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/settings?tab=financeiro&subtab=config-financeiro&financial_tab=accounts')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <span className="text-3xl">{getAccountTypeIcon(account.account_type)}</span>
            <div>
              <h1 className="text-2xl font-bold">{account.account_name}</h1>
              <p className="text-muted-foreground">
                {account.bank_name || getAccountTypeLabel(account.account_type)}
                {account.bank_agency && ` • Ag ${account.bank_agency}`}
                {account.bank_account && ` • Cc ${account.bank_account}`}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Ações da conta
                <MoreVertical className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>Editar conta</DropdownMenuItem>
              <DropdownMenuItem>Fluxo de caixa</DropdownMenuItem>
              <DropdownMenuItem>Relatório de movimentações</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Botões de conciliação apenas para contas que requerem */}
          {requiresReconciliation && (
            <>
              {!accountIntegration && (
                <Button variant="outline" onClick={() => setOfxDialogOpen(true)}>
                  <FileUp className="h-4 w-4 mr-2" />
                  Importar OFX
                </Button>
              )}

              {accountIntegration && (
                <Button onClick={handleSync} disabled={isSyncing}>
                  {isSyncing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sincronizando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Sincronizar
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* OFX Import Dialog */}
      <OFXImportDialog
        open={ofxDialogOpen}
        onOpenChange={setOfxDialogOpen}
        preselectedAccountId={accountId}
      />

      {/* Month Navigation & Summary Cards */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigateMonth('prev')}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-lg font-medium min-w-[180px] text-center">
            {format(currentMonth, "MMMM 'de' yyyy", { locale: ptBR })}
          </span>
          <Button variant="outline" size="icon" onClick={() => navigateMonth('next')}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-4">
          <Card className="px-4 py-2">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Saldo atual</p>
                <p className={`text-lg font-bold ${account.current_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(account.current_balance)}
                </p>
              </div>
            </div>
          </Card>

          {/* Card de valor pendente apenas para contas conciliáveis */}
          {requiresReconciliation && (
            <Card className="px-4 py-2">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <div>
                  <p className="text-xs text-muted-foreground">Valor pendente</p>
                  <p className="text-lg font-bold text-yellow-600">
                    {formatCurrency(pendingValue)}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {isDivergent && (
            <Card className="px-4 py-2 border-orange-300 bg-orange-50">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <div>
                  <p className="text-xs text-orange-600 font-medium">Saldo divergente</p>
                  <p className="text-lg font-bold text-orange-600">
                    {difference > 0 ? '+' : ''}{formatCurrency(difference)}
                  </p>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Tabs - Condicional baseado em requires_reconciliation */}
      {requiresReconciliation ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending" className="gap-2">
              Conciliação
              {pendingCount > 0 && (
                <Badge variant="destructive" className="rounded-full h-5 min-w-5 text-xs">
                  {pendingCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="movements" className="gap-2">
              Extrato
              <Badge variant="secondary" className="rounded-full h-5 min-w-5 text-xs">
                {bankTransactions.length}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-4">
            <AccountReconciliationPanel
              accountId={accountId!}
              startDate={startDate}
              endDate={endDate}
              onRefresh={refetchBank}
            />
          </TabsContent>

          <TabsContent value="movements" className="mt-4">
            <AccountMovementsPanel
              accountId={accountId!}
              startDate={startDate}
              endDate={endDate}
              requiresReconciliation={true}
            />
          </TabsContent>
        </Tabs>
      ) : (
        /* Conta simples: apenas Extrato */
        <AccountMovementsPanel
          accountId={accountId!}
          startDate={startDate}
          endDate={endDate}
          requiresReconciliation={false}
        />
      )}
    </div>
  );
}
